import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authApi } from '../api/auth.js'

/**
 * 認証ストア
 *
 * OTP認証フロー:
 * 1. requestOtp() - メールアドレスでOTPコード要求
 * 2. verifyOtp() - OTPコード検証してJWTトークン取得
 * 3. getCurrentUser() - ユーザー情報取得
 */
export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const loading = ref(false)
  const initialized = ref(false)

  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isDataProvider = computed(() => user.value?.account_type === 'data_provider')
  const isPlatformAdmin = computed(() => user.value?.account_type === 'platform_admin')
  const canAccessRevenue = computed(() =>
    user.value?.account_type === 'data_provider' ||
    user.value?.account_type === 'platform_admin'
  )
  const userType = computed(() => user.value?.account_type)
  const userName = computed(() => user.value?.display_name ?? '')
  const userEmail = computed(() => user.value?.email ?? '')

  /**
   * 初期化: ローカルストレージからトークンを復元し、ユーザー情報を取得
   */
  const initialize = async () => {
    if (initialized.value) return

    console.log('[AuthStore] Starting initialization...')

    try {
      const storedToken = localStorage.getItem('auth_token')
      const storedUserData = localStorage.getItem('user_data')

      console.log('[AuthStore] Stored data check:', {
        hasToken: !!storedToken,
        hasUserData: !!storedUserData
      })

      if (storedToken) {
        token.value = storedToken

        // If we have stored user data (from magic link or previous session), use it
        if (storedUserData) {
          console.log('[AuthStore] Using stored user data')
          try {
            const parsedUserData = JSON.parse(storedUserData)
            user.value = parsedUserData
            console.log('[AuthStore] Successfully parsed user data:', parsedUserData)
            // Clean up the temporary user_data storage
            localStorage.removeItem('user_data')
          } catch (e) {
            console.error('[AuthStore] Failed to parse stored user data:', e)
            // Fallback to API call
            console.log('[AuthStore] Falling back to API getCurrentUser call')
            await getCurrentUser()
          }
        } else {
          // Verify token and get user info from API
          console.log('[AuthStore] No stored user data, calling getCurrentUser API')
          try {
            await getCurrentUser()
          } catch (apiError) {
            // getCurrentUser内でエラーハンドリング済み（レート制限など）
            // 401エラーの場合のみclearAuthが呼ばれる
            console.warn('[AuthStore] getCurrentUser failed, but error already handled')
          }
        }

        console.log('[AuthStore] Final auth state after initialization:', {
          isAuthenticated: isAuthenticated.value,
          hasUser: !!user.value,
          hasToken: !!token.value,
          userId: user.value?.id
        })
      } else {
        console.log('[AuthStore] No stored token found')
      }
    } catch (error) {
      console.error('[AuthStore] Auth initialization error:', error)
      // 初期化時の予期せぬエラーのみログアウト
      // getCurrentUserのエラーは内部で処理済み
      if (error.message !== 'Failed to get user data') {
        clearAuth()
      }
    } finally {
      initialized.value = true
      console.log('[AuthStore] Initialization completed')
    }
  }

  /**
   * OTP認証コード要求
   * @param {Object} data - { email: string }
   */
  const requestOtp = async (data) => {
    loading.value = true
    try {
      console.log('[AuthStore] Requesting OTP for:', data.email)
      const response = await authApi.requestOtp(data)
      console.log('[AuthStore] OTP Request API response:', response)

      if (response.success) {
        return Promise.resolve(response)
      } else {
        throw new Error(response.message || 'OTPリクエストに失敗しました')
      }
    } catch (error) {
      console.error('[AuthStore] OTP Request error:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * OTPコード検証
   * @param {Object} data - { email: string, otp_code: string }
   */
  const verifyOtp = async (data) => {
    loading.value = true
    try {
      console.log('[AuthStore] Verifying OTP for:', data.email)
      const response = await authApi.verifyOtp(data)
      console.log('[AuthStore] OTP Verify API response:', response)

      if (response.success && response.token && response.user) {
        const { token: accessToken, user: userData } = response

        // Store token
        token.value = accessToken
        localStorage.setItem('auth_token', accessToken)

        // Store user data
        user.value = userData

        console.log('[AuthStore] OTP verification successful, user logged in:', userData)
        return Promise.resolve(response)
      } else {
        throw new Error(response.message || 'OTP認証に失敗しました')
      }
    } catch (error) {
      console.error('[AuthStore] OTP Verify error:', error)
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * マジックリンク認証
   * @param {string} magicToken - マジックリンクトークン
   */
  const verifyMagicLink = async (magicToken) => {
    loading.value = true
    try {
      console.log('[AuthStore] Verifying magic link token')
      const response = await authApi.verifyMagicLink(magicToken)
      console.log('[AuthStore] Magic Link API response:', response)

      if (response.success && response.token && response.user) {
        const { token: accessToken, user: userData } = response

        // Store token
        token.value = accessToken
        localStorage.setItem('auth_token', accessToken)

        // Store user data
        user.value = userData

        console.log('[AuthStore] Magic link verification successful, user logged in:', userData)
        return Promise.resolve(response)
      } else {
        throw new Error(response.message || 'マジックリンク認証に失敗しました')
      }
    } catch (error) {
      console.error('[AuthStore] Magic Link Verify error:', error)
      clearAuth()
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * ログアウト
   */
  const logout = async () => {
    loading.value = true
    try {
      if (token.value) {
        await authApi.logout()
      }
    } catch (error) {
      console.error('[AuthStore] Logout error:', error)
    } finally {
      clearAuth()
      loading.value = false
    }
  }

  /**
   * 現在のユーザー情報を取得
   */
  const getCurrentUser = async () => {
    if (!token.value) {
      console.error('[AuthStore] getCurrentUser called but no token available')
      throw new Error('No token available')
    }

    console.log('[AuthStore] Making getCurrentUser API call...')

    try {
      const response = await authApi.getCurrentUser()
      console.log('[AuthStore] getCurrentUser API response:', response)

      if (response.success && response.user) {
        user.value = response.user
        console.log('[AuthStore] Successfully set user data from API:', response.user)
      } else {
        console.error('[AuthStore] getCurrentUser API returned error:', response)
        throw new Error('Failed to get user data')
      }
    } catch (error) {
      console.error('[AuthStore] getCurrentUser API call failed:', error)

      // 429エラー（レート制限）の場合はログアウトさせない
      // ネットワークエラーやタイムアウトの場合もログアウトさせない
      const isRateLimitError = error.response?.status === 429
      const isNetworkError = !error.response
      const isTimeout = error.code === 'ECONNABORTED'

      if (isRateLimitError) {
        console.warn('[AuthStore] Rate limit hit (429), retaining current auth state')
      } else if (isNetworkError || isTimeout) {
        console.warn('[AuthStore] Network error or timeout, retaining current auth state')
      } else if (error.response?.status === 401) {
        // 401エラー（認証エラー）の場合のみログアウト
        console.error('[AuthStore] Authentication error (401), clearing auth')
        clearAuth()
      } else {
        // その他のエラー（500など）もログアウトさせない
        console.warn('[AuthStore] API error but retaining current auth state:', error.response?.status)
      }

      throw error
    }
  }

  /**
   * 認証情報をクリア
   */
  const clearAuth = () => {
    user.value = null
    token.value = null
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user_data')
  }

  return {
    // State
    user,
    token,
    loading,
    initialized,

    // Getters
    isAuthenticated,
    isAdmin,
    isDataProvider,
    isPlatformAdmin,
    canAccessRevenue,
    userType,
    userName,
    userEmail,

    // Actions
    initialize,
    requestOtp,
    verifyOtp,
    verifyMagicLink,
    logout,
    getCurrentUser,
    clearAuth
  }
})
